import { shouldAutoFocus } from './autoFocus'

/**
 * Handler de `onOpenAutoFocus` (Radix) que impede o overlay de focar o primeiro
 * campo de texto em aparelho de toque.
 *
 * `DialogContent`, `SheetContent` e `PopoverContent` focam o primeiro elemento
 * focável ao abrir. Quando esse elemento é um `<input>` — o que acontece em uma
 * dúzia de modais deste app e em toda a família cmdk, cujo `CommandInput` é
 * sempre o primeiro — abrir o overlay no celular sobe o teclado virtual por
 * cima do próprio conteúdo que a pessoa foi ver.
 *
 * Só cancelar o auto-foco não basta: o foco ficaria FORA do overlay, o leitor
 * de tela seguiria lendo a página atrás e o Tab começaria do lugar errado. Por
 * isso o foco vai pro container — que não é campo, então não abre teclado, e
 * mantém a navegação presa dentro do overlay.
 */
export function suppressAutoFocusOnTouch(event: Event): void {
  if (shouldAutoFocus()) return

  event.preventDefault()

  const conteudo = event.currentTarget
  if (!(conteudo instanceof HTMLElement)) return

  // Sem `tabindex` uma `div` não é focável. Não sobrescrever um valor já
  // declarado pelo componente: isso mudaria a ordem de tabulação dele.
  if (!conteudo.hasAttribute('tabindex')) conteudo.setAttribute('tabindex', '-1')
  conteudo.focus({ preventScroll: true })
}

/**
 * Compõe a supressão com um `onOpenAutoFocus` que já venha por prop.
 *
 * O handler de quem chama roda primeiro e pode cancelar sozinho; a supressão só
 * age se ninguém já tiver cancelado, pra não desfazer decisão de call site.
 */
export function composeOpenAutoFocus(
  handlerDoConsumidor?: (event: Event) => void,
): (event: Event) => void {
  return (event: Event) => {
    handlerDoConsumidor?.(event)
    if (event.defaultPrevented) return
    suppressAutoFocusOnTouch(event)
  }
}
